import React, { useEffect, useState, useMemo, Fragment } from 'react';
import { FaRegCopy, FaCheck, FaPen } from 'react-icons/fa';
import { InlineSpinner } from './LoadingState';
import { Formatter } from '../format';
import Entities from './Entities';
import { getParsedFileSummary } from '../api/api';
import { isNonSalesCall } from '../common/assessment';

// Copies an identifier and confirms it in place, so the truncated value stays
// usable without needing to be fully visible.
function CopyButton({ value, label }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      aria-label={`Copy ${label}`}
      onClick={() => {
        navigator.clipboard?.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1400);
      }}
      className="flex-shrink-0 p-1 rounded-lg text-gray-400 hover:text-cloudza-600 hover:bg-cloudza-50 transition-colors"
    >
      {copied ? <FaCheck className="text-[10px] text-coral-600" /> : <FaRegCopy className="text-[10px]" />}
    </button>
  );
}

const parseSummaryField = (data) => {
  if (!data) return { summary: '', validations: null };
  try {
    const obj = typeof data === 'string' ? JSON.parse(data) : data;
    if (typeof obj === 'object' && obj) {
      return {
        summary: obj.summary || obj.Summary || '',
        validations: obj.validations || obj.Validations || null,
      };
    }
  } catch (_) {
    /* not JSON */
  }
  return { summary: data, validations: null };
};

/**
 * Parses a summary string with a custom format like "*Key* Value".
 */
const parseAsteriskFormattedSummary = (text) => {
  if (typeof text !== 'string' || text.trim().startsWith('{')) {
    return {};
  }
  const result = {};
  const regex = /\*([^*]+)\*(.*?)(?=\*[^*]+\*|$)/gs;
  let match;
  while ((match = regex.exec(text)) !== null) {
    const key = match[1].trim();
    let value = match[2].trim();
    const lowerKey = key.toLowerCase();
    if (lowerKey === 'actions') {
      result[lowerKey] = value
        .split('\n')
        .map((line) => line.trim().replace(/^-/, '').trim())
        .filter(Boolean);
    } else {
      result[lowerKey] = value;
    }
  }
  return result;
};

const formatAsteriskWrappedText = (text) => {
  if (!text) return null;
  const segments = text.split(/(\*[^*]+\*)/g);
  return segments.map((segment, index) => {
    if (!segment) return null;
    if (segment.startsWith('*') && segment.endsWith('*') && segment.length > 2) {
      return (
        <strong key={`bold-${index}`} className="font-semibold">
          {segment.slice(1, -1)}
        </strong>
      );
    }
    return <Fragment key={`text-${index}`}>{segment}</Fragment>;
  });
};

// Agent core writes its verdict into Summary as OverallAssessment /
// overall_assessment — {summary, confidence} as an object or a JSON string.
const SUMMARY_FAILED_PLACEHOLDER = 'Click reload to generate';
const getOverallAssessment = (callData) => {
  const s = callData?.ConversationAnalytics?.Summary;
  if (!s) return null;
  const key = Object.keys(s).find(
    (k) => k.toString().trim().toLowerCase().replace(/[_\s]/g, '') === 'overallassessment'
  );
  if (!key) return null;
  let v = s[key];
  if (typeof v === 'string') {
    try {
      v = JSON.parse(v);
    } catch {
      return { summary: v };
    }
  }
  return v && typeof v === 'object' && !Array.isArray(v) ? v : null;
};

const CallDetailsPanel = ({ callData, onEditMetadata }) => {
  const parsedAsteriskSummary = useMemo(
    () => parseAsteriskFormattedSummary(callData?.summary_summary),
    [callData?.summary_summary]
  );

  const { summary: parsedInitialSummary } = useMemo(
    () => parseSummaryField(callData?.summary_summary),
    [callData?.summary_summary]
  );

  const overallAssessment = useMemo(() => getOverallAssessment(callData), [callData]);

  const storedInitialSummary =
    parsedAsteriskSummary.summary ||
    parsedInitialSummary ||
    callData?.ConversationAnalytics?.ContactSummary?.AutoGenerated?.OverallSummary?.Content ||
    '';
  // The summarize step stores the literal placeholder when it failed; the
  // agent-core assessment carries the real prose in that case.
  const initialSummaryText =
    storedInitialSummary && storedInitialSummary !== SUMMARY_FAILED_PLACEHOLDER
      ? storedInitialSummary
      : overallAssessment?.summary || storedInitialSummary;

  const [summaryText, setSummaryText] = useState(initialSummaryText);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState(null);

  const formattedSummaryText = useMemo(
    () => formatAsteriskWrappedText(summaryText),
    [summaryText]
  );

  const genAiSummary = useMemo(() => {
    if (!callData) return [];
    const hiddenLabels = [
      'validations',
      'transcription',
      'contact summary',
      'topic',
      'product',
      'resolved',
      'actions',
      'overall assessment',
      'overall_assessment',
      'overallassessment',
      // The main summary paragraph above renders this same value (with the
      // agent-core assessment as fallback) — repeating it here reads as a
      // second, contradictory summary.
      'summary',
    ];
    let summary = callData?.ConversationAnalytics?.Summary
      ? Object.entries(callData.ConversationAnalytics.Summary).map(
          ([key, value]) => ({
            label: key,
            value: Array.isArray(value) ? value.join(', ') : value,
          })
        )
      : [];

    const contactSummary =
      callData?.ConversationAnalytics?.ContactSummary?.AutoGenerated?.OverallSummary?.Content;
    if (contactSummary) {
      summary = [{ label: 'Contact Summary', value: contactSummary }, ...summary];
    }

    return summary.filter(
      (e) => {
        const normalized = (e.label ?? '')
          .toString()
          .trim()
          .toLowerCase()
          .replace(/:$/, '');
        if (hiddenLabels.includes(normalized)) return false;
        // A failed generation stores this literal placeholder as the value —
        // never worth rendering as an "additional detail".
        const value = (e.value ?? '').toString().trim();
        return value !== '' && value !== SUMMARY_FAILED_PLACEHOLDER;
      }
    );
  }, [callData]);

  useEffect(() => {
    if (!callData) return;
    const assessmentSummary = getOverallAssessment(callData)?.summary;
    const withAssessmentFallback = (stored) =>
      stored && stored !== SUMMARY_FAILED_PLACEHOLDER
        ? stored
        : assessmentSummary || stored;
    const s = callData.summary_summary;
    if (s) {
      const parsed = parseSummaryField(s);
      const asteriskParsed = parseAsteriskFormattedSummary(s);
      setSummaryText(
        withAssessmentFallback(
          asteriskParsed.summary ||
            parsed.summary ||
            callData?.ConversationAnalytics?.ContactSummary?.AutoGenerated?.OverallSummary?.Content ||
            ''
        )
      );
      return;
    }

    const contactSummary =
      callData?.ConversationAnalytics?.ContactSummary?.AutoGenerated?.OverallSummary?.Content;
    if (contactSummary) {
      setSummaryText(withAssessmentFallback(contactSummary));
      return;
    }

    if (assessmentSummary) {
      setSummaryText(assessmentSummary);
      return;
    }

    let cancelled = false;
    const load = async () => {
      try {
        setSummaryLoading(true);
        const filename = callData.key || callData.filename;
        if (!filename) {
          console.warn('No filename available for parsed summary.');
          return;
        }
        const parsed = await getParsedFileSummary(filename);
        if (cancelled) return;
        const parsedField = parseSummaryField(
          parsed?.summary_summary || parsed?.summary
        );
        const asteriskParsed = parseAsteriskFormattedSummary(
          parsed?.summary_summary || parsed?.summary
        );
        const text =
          asteriskParsed.summary ||
          parsedField.summary ||
          parsed?.ConversationAnalytics?.ContactSummary?.AutoGenerated?.OverallSummary?.Content;
        if (text) {
          setSummaryText(text);
        } else {
          setSummaryError('Summary unavailable.');
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) setSummaryError('Failed to load summary.');
      } finally {
        if (!cancelled) setSummaryLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [callData]);

  if (!callData) {
    return (
      <div className="text-sm text-gray-500 !text-left" style={{ textAlign: 'left' }}>
        Select a call to view details.
      </div>
    );
  }

  const metadata = [
    {
      label: 'Timestamp',
      value: Formatter.Timestamp(
        callData.timestamp || callData?.ConversationAnalytics?.ConversationTime
      ),
    },
    {
      label: 'Job ID',
      value:
        callData.jobName ||
        callData?.ConversationAnalytics?.SourceInformation?.[0]?.TranscribeJobInfo
          ?.TranscriptionJobName,
    },
    { label: 'GUID', value: callData.guid || callData?.ConversationAnalytics?.GUID },
    { label: 'Conversation ID', value: callData.conversationId || callData?.ConversationAnalytics?.Telephony?.Genesys?.conversationId },
    { label: 'DNIS', value: callData.dnis || callData?.ConversationAnalytics?.Telephony?.Genesys?.dnis },
    { label: 'Agent', value: callData.agent || callData?.ConversationAnalytics?.Agent },
    { label: 'Campaign', value: callData.customer || callData?.ConversationAnalytics?.Cust },
    { label: 'Queue', value: callData.queue || callData?.ConversationAnalytics?.Queue },
    {
      label: 'Language',
      value: callData.lang || callData?.ConversationAnalytics?.LanguageCode,
    },
    {
      label: 'Duration',
      value: Formatter.Time(
        callData.duration || callData?.ConversationAnalytics?.Duration
      ),
    },
    {
      label: 'Assessed By',
      value: callData.assessedBy || callData?.ConversationAnalytics?.AssessDetails?.assessedBy || '-',
    },
    {
      label: 'Assessed At',
      value: callData.assessedAt
        ? Formatter.Timestamp(callData.assessedAt)
        : (callData?.ConversationAnalytics?.AssessDetails?.assessedAt
            ? Formatter.Timestamp(callData.ConversationAnalytics.AssessDetails.assessedAt)
            : '-'),
    },
    {
      // Marked non-sales by a QA manager - left out of scores. Full results or a list record.
      label: 'Call Type',
      value: (isNonSalesCall(callData) || callData.nonSales === true) ? 'Non-sales' : 'Sales',
    },
  ];

  const topic =
    parsedAsteriskSummary.topic ||
    callData.summary_topic ||
    callData?.ConversationAnalytics?.ContactSummary?.Topic ||
    callData?.ConversationAnalytics?.Summary?.Topic ||
    '-';

  const product =
    parsedAsteriskSummary.product ||
    callData.summary_product ||
    callData?.ConversationAnalytics?.ContactSummary?.Product ||
    callData?.ConversationAnalytics?.Summary?.Product ||
    '-';

  const resolved =
    parsedAsteriskSummary.resolved ||
    callData.summary_resolved ||
    callData?.ConversationAnalytics?.ContactSummary?.Resolved ||
    callData?.ConversationAnalytics?.Summary?.Resolved ||
    '-';

  const actions =
    parsedAsteriskSummary.actions ||
    (Array.isArray(callData.summary_actions)
      ? callData.summary_actions
      : Array.isArray(callData?.ConversationAnalytics?.Summary?.Actions)
      ? callData.ConversationAnalytics.Summary.Actions
      : Array.isArray(callData?.ConversationAnalytics?.ActionItemsDetected)
      ? callData.ConversationAnalytics.ActionItemsDetected.map((a) => a.Text)
      : []);

  const entities = Array.isArray(callData.entities)
    ? callData.entities
    : callData.analytics?.entities || [];

  const transcript = callData.transcript || '';

  return (
    // Global hard left alignment (inline style wins against rogue CSS)
    <div
      className="space-y-6 !text-left"
      style={{ textAlign: 'left' }}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Metadata */}
        <div className="card !text-left" style={{ textAlign: 'left' }}>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h2 className="text-sm font-bold text-gray-900">Metadata</h2>
            {onEditMetadata && (
              <button
                onClick={onEditMetadata}
                className="btn-outline !text-xs !py-1 !px-2"
              >
                <FaPen className="text-[10px]" />
                Edit
              </button>
            )}
          </div>
          {/* Identifiers are long enough to wrap over many lines and swamp the
              card, so they're kept to one truncated monospace line with the
              full value on hover and a copy action — which is what they're
              actually wanted for. */}
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6 !text-left">
            {metadata.map((m) => {
              const value = m.value || '-';
              const isId = /id$|guid/i.test(m.label) && value !== '-';
              return (
                <div key={m.label} className="space-y-1 !text-left min-w-0">
                  <dt className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">{m.label}</dt>
                  <dd className="text-sm text-gray-900 min-w-0">
                    {isId ? (
                      <span className="flex items-center gap-1.5 min-w-0">
                        <span className="font-mono text-xs truncate" title={value}>{value}</span>
                        <CopyButton value={value} label={m.label} />
                      </span>
                    ) : (
                      <span className="break-words">{value}</span>
                    )}
                  </dd>
                </div>
              );
            })}
          </dl>
        </div>

        {/* Summary */}
        <div className="card !text-left" style={{ textAlign: 'left' }}>
          <h2 className="text-sm font-bold text-gray-900 mb-4">Summary</h2>

          {/* Basic Info Section */}
          <dl className="grid grid-cols-1 sm:grid-cols-3 gap-y-3 gap-x-6 mb-4 pb-4 border-b border-gray-100 !text-left">
            <div>
              <dt className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Topic</dt>
              <dd className="text-sm text-gray-900 mt-1 break-words">{topic}</dd>
            </div>
            <div>
              <dt className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Product</dt>
              <dd className="text-sm text-gray-900 mt-1 break-words">{product}</dd>
            </div>
            <div>
              <dt className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Resolved</dt>
              <dd className="mt-1">
                {String(resolved).toLowerCase() === 'yes' ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-50 text-green-700">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500" /> Yes
                  </span>
                ) : String(resolved).toLowerCase() === 'no' ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-50 text-red-700">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500" /> No
                  </span>
                ) : (
                  <span className="text-sm text-gray-900">{resolved}</span>
                )}
              </dd>
            </div>
          </dl>

          {/* Summary Text Section */}
          {summaryLoading ? (
            <p className="flex items-center gap-2 text-sm text-gray-500 !text-left">
              <InlineSpinner /> Loading summary
            </p>
          ) : summaryText ? (
            <div className="mb-4 !text-left">
              <p className="text-sm text-gray-700 whitespace-pre-line !text-left">{formattedSummaryText}</p>
            </div>
          ) : summaryError ? (
            <p className="text-sm text-red-700 mb-4 !text-left">({summaryError})</p>
          ) : genAiSummary.length === 0 ? (
            // Only claim "no summary" when the Additional Details block below
            // isn't about to show one — the two together read as a contradiction.
            <p className="text-sm text-gray-500 mb-4 !text-left">No summary available.</p>
          ) : null}

          {/* Actions Section */}
          {actions.length > 0 && (
            <div className="mb-4 !text-left">
              <p className="text-sm font-medium text-gray-500 mb-2">Actions:</p>
              <ul className="space-y-1 list-disc pl-5">
                {actions.map((action, i) => (
                  <li key={i} className="text-sm text-gray-700 break-words">
                    {action}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Additional AI Summary Section — only draws its own divider when
              summary text/actions render above it; otherwise the Topic row's
              bottom border already separates them (two borders = double line). */}
          {genAiSummary.length > 0 && (
            <div className={`${summaryText || summaryLoading || summaryError || actions.length > 0 ? 'border-t border-gray-100 pt-4' : ''} !text-left`}>
              <p className="text-sm font-medium text-gray-500 mb-2">Additional Details:</p>
              <dl className="space-y-2">
                {genAiSummary.map((entry, i) => (
                  <div key={i}>
                    <dt className="text-sm font-medium text-gray-700 inline">{entry.label}: </dt>
                    <dd className="text-sm text-gray-600 inline">{entry.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Entities */}
        {/* <div className="card !text-left" style={{ textAlign: 'left' }}>
          <h2 className="text-lg font-semibold mb-4">Entities</h2>
          <Entities entities={entities} />
        </div> */}

        {/* Transcription */}
        {/* <div className="card !text-left" style={{ textAlign: 'left' }}>
          <h2 className="text-lg font-semibold mb-4">Transcription</h2>
          {transcript ? (
            <pre className="whitespace-pre-wrap text-sm text-gray-800 font-mono">
              {transcript}
            </pre>
          ) : (
            <p className="text-sm text-gray-500">Transcript not available.</p>
          )}
        </div> */}
      </div>
    </div>
  );
};

export default CallDetailsPanel;